import { cn, cva } from '@/lib'
import { Slot } from '@radix-ui/react-slot'
import type { VariantProps } from 'cva'
import { LoaderCircle } from 'lucide-react'
import * as React from 'react'

const resetSize = 'h-auto p-0'
const buttonVariants = cva({
	base: 'cursor-pointer inline-flex items-center justify-center whitespace-nowrap rounded-md text-base font-medium transition-colors focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-auto',
	variants: {
		variant: {
			primary:
				'bg-gray-950 text-white hover:bg-purple-700 aria-expanded:bg-purple-700 disabled:bg-gray-200',
			secondary:
				'text-gray-900 border border-gray-400 hover:text-purple-700 hover:border-purple-700 aria-expanded:text-purple-700 aria-expanded:border-purple-700 disabled: border-gray-200 disabled:text-gray-200 disabled:hover:border-gray-200',
			tertiary:
				'text-purple-800 hover:text-gray-900 hover:bg-purple-50 disabled:text-gray-300 disabled:hover:bg-transparent',
			destructive:
				'bg-destructive text-destructive-foreground shadow-xs hover:bg-destructive/90',
			text: 'bg-transparent text-link hover:bg-blue-50 disabled:text-blue-300 disabled:hover:bg-transparent',
			textDestructive:
				'bg-transparent text-red-600 hover:bg-red-50 disabled:text-red-300 disabled:hover:bg-transparent',
			sideMenu:
				'block whitespace-normal text-left rounded-none px-6 py-2 w-full font-bold aria-pressed:bg-gray-750 aria-pressed:text-snow disabled:text-gray-300 hover:bg-purple-50 disabled:hover:bg-transparent',
			card: 'block whitespace-normal text-left grid grid-cols-1 items-stretch p-[16px] border border-gray-200 bg-white aria-pressed:p-[15px] aria-pressed:border-2 aria-pressed:border-purple-700',
		},
		size: {
			default: 'h-8 px-3 py-2',
			sm: 'h-8 rounded-md px-3 text-xs',
			lg: 'h-12 rounded-md px-6 text-lg',
			'2lg': 'h-14 rounded-md px-6 text-2lg font-normal',
			icon: 'h-9 w-9 px-0 text-purple-700 bg-transparent hover:bg-transparent aria-expanded:bg-transparent',
			'icon-sm':
				'w-6 h-6 px-0 text-purple-700 bg-transparent hover:bg-transparent aria-expanded:bg-transparent',
			block: '',
		},
	},
	defaultVariants: {
		size: 'default',
		variant: 'primary',
	},
})

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean
	loading?: boolean
	active?: boolean
	iconPosition?: 'left' | 'right'
	icon?: React.ReactNode
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{
			className,
			variant,
			size,
			asChild = false,
			children,
			loading,
			disabled,
			active = undefined,
			icon,
			iconPosition = 'left',
			...props
		},
		ref,
	) => {
		const Comp = asChild ? Slot : 'button'
		const isIconSized = size?.includes('icon')

		const Icon = (loading || icon) && (
			<div
				className={cn(
					isIconSized ? undefined : iconPosition === 'left' ? 'mr-2' : 'ml-2',
				)}
			>
				{loading ? <LoaderCircle className="h-4 w-4 animate-spin" /> : icon}
			</div>
		)

		return (
			<Comp
				className={cn(
					buttonVariants({
						size:
							size ||
							(['sideMenu', 'card'].includes(variant || '')
								? 'block'
								: 'default'),
						variant,
						className,
					}),
				)}
				ref={ref}
				aria-pressed={active}
				disabled={loading || disabled}
				{...props}
			>
				{iconPosition === 'left' && Icon}
				{children}
				{iconPosition === 'right' && Icon}
			</Comp>
		)
	},
)
Button.displayName = 'Button'

export { Button, buttonVariants }
